// src/redux/services/adminApi.js
// ✅ FIXED & COMPLETE ADMIN API

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logout } from "../slices/authSlice"; // Adjust path as needed
import { accumulateDevicePages } from "../../utils/devicePagination";
import { adaptV2DeviceRows } from "../../utils/deviceV2Adapter";

const normalizeFleetStatusKey = (value) => {
  const key = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!key || key === "all") return "";
  if (key === "moving" || key === "online") return "running";
  if (key === "idling" || key === "engine_on_stopped") return "idle";
  if (key === "parked" || key === "engine_off") return "stopped";
  if (
    key === "inactive" ||
    key === "offline" ||
    key === "disconnect" ||
    key === "disconnected" ||
    key === "not_reporting" ||
    key === "notreporting" ||
    key === "no_reporting"
  ) {
    return "inactive";
  }
  if (key === "nodata" || key === "unknown") return "no_data";
  return key;
};

const baseQuery = fetchBaseQuery({
  // ✅ REACT_APP_BACKEND_URL = https://qylmox.in/api (already has /api)
  baseUrl: process.env.REACT_APP_BACKEND_URL || "http://localhost:7600/api",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem("token");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("x-auth-token", token);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    const status = result.error.status;

    // 🔴 ONLY logout when token is invalid
    if (status === 401) {
      api.dispatch(logout());
    }

    // 🟡 Permission issue → DO NOT logout
    if (status === 403) {
      console.warn(
        "Permission denied:",
        result.error.data?.msg || "Not authorized"
      );
    }
  }

  return result;
};

const PERSISTED_DEVICE_CACHE_TTL_MS = 5 * 60 * 1000;

const buildPersistedDeviceCacheKey = (prefix, params = {}) => {
  const sortedEntries = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .sort(([a], [b]) => a.localeCompare(b));
  return `${prefix}:${JSON.stringify(sortedEntries)}`;
};

const buildAuthScopedDeviceCachePrefix = (prefix, authState = {}) => {
  const user = authState?.user || null;
  const token = String(authState?.token || "");
  const userId = String(user?._id || user?.id || "anonymous");
  const role = String(user?.role || "unknown");
  const tokenHint = token ? token.slice(-16) : "no-token";
  return `${prefix}:${userId}:${role}:${tokenHint}`;
};

const readPersistedDeviceCache = (cacheKey, ttlMs = PERSISTED_DEVICE_CACHE_TTL_MS) => {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);
    if (!createdAt || Date.now() - createdAt > ttlMs) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    return Array.isArray(parsed?.data) ? parsed.data : null;
  } catch (_) {
    return null;
  }
};

const writePersistedDeviceCache = (cacheKey, data) => {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        createdAt: Date.now(),
        data: Array.isArray(data) ? data : [],
      })
    );
  } catch (_) {
    // Ignore storage failures quietly.
  }
};

const clearPersistedDeviceCachesByPrefixes = (prefixes = []) => {
  try {
    if (typeof localStorage === "undefined") return;
    const normalizedPrefixes = prefixes
      .map((prefix) => String(prefix || "").trim())
      .filter(Boolean);
    if (!normalizedPrefixes.length) return;

    const keysToRemove = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key) continue;
      if (normalizedPrefixes.some((prefix) => key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (_) {
    // Ignore storage cleanup failures quietly.
  }
};

const buildPagedDeviceAccumulatorQuery = ({
  pageLimit = 200,
  defaultLite = false,
  defaultIncludeRelations = false,
  allowPartialOnLaterError = true,
  concurrency = 4,
  persistKeyPrefix = "",
  persistTtlMs = PERSISTED_DEVICE_CACHE_TTL_MS,
} = {}) =>
  async (arg, _api, _extra, fetchWithBQ) => {
    const params = (arg && typeof arg === "object") ? { ...arg } : {};
    if (defaultLite && params.lite === undefined) {
      params.lite = "true";
    }
    if (defaultIncludeRelations && params.includeRelations === undefined) {
      params.includeRelations = "true";
    }
    const authScopedPersistPrefix = persistKeyPrefix
      ? buildAuthScopedDeviceCachePrefix(persistKeyPrefix, _api?.getState?.()?.auth)
      : "";
    const persistCacheKey = authScopedPersistPrefix
      ? buildPersistedDeviceCacheKey(authScopedPersistPrefix, params)
      : "";
    const persistedRows = persistCacheKey
      ? readPersistedDeviceCache(persistCacheKey, persistTtlMs)
      : null;

    if (persistedRows && persistedRows.length > 0) {
      return { data: persistedRows };
    }

    if (persistCacheKey && Array.isArray(persistedRows) && persistedRows.length === 0) {
      try {
        localStorage.removeItem(persistCacheKey);
      } catch (_) {
        // Ignore storage cleanup failures quietly.
      }
    }

    const extra = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== "")
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join("&");

    let rows = [];
    const currentRole = String(_api?.getState?.()?.auth?.user?.role || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");
    const canUseAdminDeviceFallback = !["user", "client", "viewer"].includes(currentRole);
    try {
      rows = await accumulateDevicePages({
        pageLimit,
        concurrency,
        allowPartialOnLaterError,
        fetchPage: async (page) => {
          const qs = `page=${page}&limit=${pageLimit}${extra ? `&${extra}` : ""}`;
          const result = await fetchWithBQ({ url: `/devices?${qs}` });
          if (result.error) {
            throw result.error;
          }
          return result.data;
        },
      });

      if (rows.length === 0 && canUseAdminDeviceFallback) {
        const adminRows = await accumulateDevicePages({
          pageLimit,
          concurrency,
          allowPartialOnLaterError,
          fetchPage: async (page) => {
            const qs = `page=${page}&limit=${pageLimit}${extra ? `&${extra}` : ""}`;
            const result = await fetchWithBQ({ url: `/admin/devices/v2?${qs}` });
            if (result.error) {
              throw result.error;
            }
            return result.data;
          },
        });
        rows = adaptV2DeviceRows(adminRows);
      }
    } catch (error) {
      return { error };
    }

    if (persistCacheKey) {
      writePersistedDeviceCache(persistCacheKey, rows);
    }

    return { data: rows };
  };

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['AdminData', 'Asset', 'Users', 'Device', 'Devices', 'Geofence', 'Landmark', 'UserAlertPrefs'],
  endpoints: (builder) => ({
    
    // ═══════════════════════════════════════════════════════════════════
    // USER ENDPOINTS
    // ═══════════════════════════════════════════════════════════════════
    
    // GET /admin/dashboard
    getAdminData: builder.query({
      query: () => ({ url: '/admin/dashboard' }), 
      providesTags: ['AdminData'],
    }),
    
    // POST /admin/users
    createUser: builder.mutation({
      query: (userData) => ({
        url: '/admin/users',
        method: 'POST',
        body: userData,
      }),
invalidatesTags: [{ type: "Users", id: "LIST" }],
    }),

    // GET /admin/users
  getAllUsers: builder.query({
  query: () => ({ url: "/admin/users" }),
  transformResponse: (response) => {
    // normalize
    return response?.data || response || [];
  },
  providesTags: (result) =>
    result
      ? [
          { type: "Users", id: "LIST" },
          ...result.map((u) => ({ type: "Users", id: u._id || u.id })),
        ]
      : [{ type: "Users", id: "LIST" }],
}),


// POST /admin/users (sub_admin helper)
createSubAdmin: builder.mutation({
  query: (userData) => ({
    url: "/admin/users",
    method: "POST",
    body: { ...userData, role: "sub_admin" },
  }),
  invalidatesTags: [{ type: "Users", id: "LIST" }],
}),



    // ✅ GET /admin/assignable-users - FIXED URL
    getAssignableUsers: builder.query({
      query: (roles) => {
        // roles is an array like ['viewer', 'client', 'user']
        const rolesString = Array.isArray(roles) ? roles.join(',') : roles;
        return {
          url: `/admin/assignable-users`,
          params: { roles: rolesString }
        };
      },
      transformResponse: (response) => {
        // Backend returns { data: [...] }
        return response?.data || response || [];
      },
      keepUnusedDataFor: 0, // Fresh data every time
    }),
    
    // PUT /admin/users/:userId
    editUser: builder.mutation({
      query: ({ userId, ...patch }) => ({
        url: `/admin/users/${userId}`,
        method: 'PUT',
        body: patch,
      }),
invalidatesTags: (result, error, { userId }) => [
  { type: "Users", id: "LIST" },
  { type: "Users", id: userId },
],


    }),

    // DELETE /admin/users/:userId
 deleteUser: builder.mutation({
  query: (userId) => ({
    url: `/admin/users/${userId}`,
    method: "DELETE",
  }),
  invalidatesTags: (result, error, userId) => [
    { type: "Users", id: "LIST" },
    { type: "Users", id: userId },
  ],
}),


    // PUT /admin/users/:userId/restore
    restoreUser: builder.mutation({
  query: (userId) => ({
    url: `/admin/users/${userId}/restore`,
    method: "PUT",
    body: {},
  }),
  invalidatesTags: (result, error, userId) => [
    { type: "Users", id: "LIST" },
    { type: "Users", id: userId },
  ],
}),

    
    // PUT /admin/users/:userId/reset-password
    resetPassword: builder.mutation({
      query: ({ userId, password }) => ({
        url: `/admin/users/${userId}/reset-password`,
        method: 'PUT',
        body: { password },
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: 'User', id: userId }
      ], 
    }),

    getDeviceNotificationTemplate: builder.query({
      query: (deviceId) => ({
        url: `/users/devices/${deviceId}/alerts`,
      }),
      providesTags: (result, error, deviceId) => [
        { type: "UserAlertPrefs", id: `device-template-${deviceId}` },
      ],
    }),

    getManagedUserDeviceAlertPreferences: builder.query({
      query: ({ userId, deviceId }) => ({
        url: `/users/${userId}/devices/${deviceId}/alerts`,
      }),
      providesTags: (result, error, { userId, deviceId }) => [
        { type: "UserAlertPrefs", id: `${userId}:${deviceId}` },
      ],
    }),

    getManagedUserAlertSummary: builder.query({
      async queryFn({ userId, deviceIds = [] }, _api, _extraOptions, fetchWithBQ) {
        if (!userId || !Array.isArray(deviceIds) || deviceIds.length === 0) {
          return { data: { alerts: [], ownerUser: null, targetUser: null, deviceCount: 0 } };
        }

        const normalizedDeviceIds = Array.from(
          new Set(deviceIds.map((value) => String(value || "").trim()).filter(Boolean))
        );
        const referenceDeviceId = normalizedDeviceIds[0];
        const result = await fetchWithBQ({
          url: `/users/${userId}/devices/${referenceDeviceId}/alerts`,
        });

        if (result.error) {
          return { error: result.error };
        }

        const raw = result.data || {};
        const alerts = (Array.isArray(raw?.alerts) ? raw.alerts : [])
          .map((row, index) => ({
            key: row?.key || row?.alarmType || `alert_${index}`,
            alarmType: row?.alarmType || row?.key || `alert_${index}`,
            label: row?.label || row?.alarmType || `Alert ${index + 1}`,
            checked: Boolean(row?.checked),
            partiallyChecked: false,
            inherited: Boolean(row?.inherited),
          }))
          .sort((left, right) => String(left.label || "").localeCompare(String(right.label || "")));

        return {
          data: {
            alerts,
            ownerUser: raw?.ownerUser || null,
            targetUser: raw?.targetUser || null,
            deviceCount: normalizedDeviceIds.length,
            deviceIds: normalizedDeviceIds,
            referenceDeviceId,
          },
        };
      },
      providesTags: (result, error, { userId }) => [
        { type: "UserAlertPrefs", id: `managed-summary-${userId}` },
      ],
    }),

    updateManagedUserDeviceAlertPreferences: builder.mutation({
      query: ({ userId, deviceId, alerts }) => ({
        url: `/users/${userId}/devices/${deviceId}/alerts`,
        method: "PUT",
        body: { alerts },
      }),
      invalidatesTags: (result, error, { userId, deviceId }) => [
        { type: "UserAlertPrefs", id: `${userId}:${deviceId}` },
      ],
    }),

    updateManagedUserAlertSummary: builder.mutation({
      async queryFn({ userId, deviceIds = [], alarmType, checked }, _api, _extraOptions, fetchWithBQ) {
        if (!userId || !alarmType || !Array.isArray(deviceIds) || deviceIds.length === 0) {
          return { error: { status: "CUSTOM_ERROR", data: "User id, alarm type, and device ids are required" } };
        }

        const normalizedDeviceIds = Array.from(
          new Set(deviceIds.map((value) => String(value || "").trim()).filter(Boolean))
        );
        const referenceDeviceId = normalizedDeviceIds[0];
        const currentResult = await fetchWithBQ({
          url: `/users/${userId}/devices/${referenceDeviceId}/alerts`,
        });

        if (currentResult.error) {
          return { error: currentResult.error };
        }

        const currentAlerts = Array.isArray(currentResult.data?.alerts) ? currentResult.data.alerts : [];
        const nextAlerts = currentAlerts
          .map((row) =>
            row?.alarmType === alarmType ? { ...row, checked: Boolean(checked) } : row
          )
          .filter((row) => row?.checked)
          .map((row) => row?.alarmType)
          .filter(Boolean);

        const updateResult = await fetchWithBQ({
          url: `/users/${userId}/devices/${referenceDeviceId}/alerts`,
          method: "PUT",
          body: {
            alerts: nextAlerts,
            applyGlobally: true,
          },
        });

        if (updateResult.error) {
          return { error: updateResult.error };
        }

        return {
          data: {
            success: true,
            referenceDeviceId,
            applyGlobally: true,
          },
        };
      },
      invalidatesTags: (result, error, { userId, deviceIds = [] }) => [
        { type: "UserAlertPrefs", id: `managed-summary-${userId}` },
        ...deviceIds.map((deviceId) => ({ type: "UserAlertPrefs", id: `${userId}:${deviceId}` })),
      ],
    }),

    revealUserPassword: builder.mutation({
      query: (userId) => ({
        url: `/admin/users/${userId}/password`,
        method: "GET",
      }),
    }),

    // POST /admin/assign-user-to-group-admin/:groupAdminId
    assignUserToGroupAdmin: builder.mutation({
      query: ({ groupAdminId, userIdToManage }) => ({
        url: `/admin/assign-user-to-group-admin/${groupAdminId}`,
        method: 'POST',
        body: { userIdToManage },
      }),
      invalidatesTags: ['AdminData'],
    }),
    
    // PUT /admin/group-admins/:groupAdminId/remove-managed-user
    removeManagedUser: builder.mutation({
      query: ({ groupAdminId, userIdToRemove }) => ({
        url: `/admin/group-admins/${groupAdminId}/remove-managed-user`,
        method: 'PUT',
        body: { userIdToRemove },
      }),
      invalidatesTags: ['AdminData'],
    }),
    
    // POST /auth/impersonate/:id
    impersonateUser: builder.mutation({
      query: (userId) => ({
        url: `/auth/impersonate/${userId}`, 
        method: 'POST',
        body: {},
      }),
    }),

    // ═══════════════════════════════════════════════════════════════════
    // DEVICE ENDPOINTS
    // ═══════════════════════════════════════════════════════════════════
    getViewportVehicles: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const params = {
          north: arg?.north,
          south: arg?.south,
          east: arg?.east,
          west: arg?.west,
          zoom: arg?.zoom,
          filters: arg?.filters || "all",
          search: arg?.search || "",
        };

        const normalizeMapPayload = (payload, source) => {
          const data = payload?.data || payload || {};
          const items = Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.data?.items)
              ? data.data.items
              : [];
          const vehicles = Array.isArray(data?.vehicles)
            ? data.vehicles
            : Array.isArray(data?.data?.vehicles)
              ? data.data.vehicles
              : [];
          const clusters = Array.isArray(data?.clusters)
            ? data.clusters
            : Array.isArray(data?.data?.clusters)
              ? data.data.clusters
              : [];
          const mergedItems = items.length
            ? items
            : [
                ...clusters.map((item) => ({ ...item, type: item?.type || "cluster" })),
                ...vehicles.map((item) => ({ ...item, type: item?.type || "vehicle" })),
              ];
          return {
            items: mergedItems,
            total: Number(data?.total || mergedItems.length || 0),
            mode: data?.mode || (clusters.length > 0 ? "cluster" : "vehicles"),
            source,
          };
        };

        const primary = await fetchWithBQ({ url: "/map/viewport", params });
        if (!primary.error) {
          const normalized = normalizeMapPayload(primary.data, "viewport");
          if (normalized.items.length > 0 || Number(normalized.total || 0) > 0) {
            return { data: normalized };
          }
        }

        const fallbackVehicles = await fetchWithBQ({ url: "/map/vehicles", params });
        if (!fallbackVehicles.error) {
          const normalized = normalizeMapPayload(fallbackVehicles.data, "map-vehicles");
          if (normalized.items.length > 0 || Number(normalized.total || 0) > 0) {
            return { data: normalized };
          }
        }

        const legacyMap = await fetchWithBQ({
          url: "/devices/map",
          params: {
            page: 1,
            limit: 250,
            ...params,
          },
        });
        if (!legacyMap.error) {
          const rows = Array.isArray(legacyMap.data?.data) ? legacyMap.data.data : [];
          return {
            data: {
              items: rows.map((item) => ({ ...item, type: "vehicle" })),
              total: Number(legacyMap.data?.total || rows.length || 0),
              mode: "vehicles",
              source: "devices-map",
            },
          };
        }

        return { error: primary.error || fallbackVehicles.error || legacyMap.error };
      },
      keepUnusedDataFor: 30,
    }),
    
    // GET /devices - Single page fetch (fast path for table pagination when no search/filter)
    // Returns { data: [...], total, hasMore, page, limit } so the UI can show correct totals.
    getDevicesPage: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const params = {
          page: arg?.page ?? 1,
          limit: arg?.limit ?? 10,
          includeRelations: "true",
          ...arg,
        };
        const normalizedStatus = normalizeFleetStatusKey(params.status);
        if (params.status) {
          params.status = normalizedStatus || params.status;
        }

        const hasStatusFilter = Boolean(normalizedStatus);
        const hasStructuredDeviceSearch =
          Boolean(String(params.search || "").trim()) ||
          Boolean(String(params.filterBy || "").trim()) ||
          Boolean(String(params.dealer || "").trim()) ||
          Boolean(String(params.customer || "").trim()) ||
          Boolean(String(params.group || "").trim()) ||
          Boolean(String(params.summaryTab || "").trim());
        const endpoint = hasStatusFilter || hasStructuredDeviceSearch ? "/devices/v2/page" : "/devices";
        const response = await fetchWithBQ({ url: endpoint, params });
        const currentRole = String(_api?.getState?.()?.auth?.user?.role || "")
          .trim()
          .toLowerCase()
          .replace(/[\s-]+/g, "_");
        const canUseAdminFallback = !["user", "client", "viewer"].includes(currentRole);

        const normalizeRows = (payload) =>
          Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload?.data?.data)
                ? payload.data.data
                : [];

        let payload = response.data;
        let rows = normalizeRows(payload);

        if ((response.error || rows.length === 0) && canUseAdminFallback && !hasStatusFilter) {
          const adminFallback = await fetchWithBQ({ url: "/admin/devices/v2", params });
          if (!adminFallback.error) {
            payload = adminFallback.data;
            rows = adaptV2DeviceRows(adminFallback.data?.data);
          } else if (response.error) {
            return { error: response.error };
          }
        } else if (response.error) {
          return { error: response.error };
        }

        const total = Number(payload?.total || payload?.data?.total || payload?.count || rows.length) || rows.length;
        const hasMore = Boolean(payload?.hasMore ?? payload?.data?.hasMore);

        return {
          data: {
            data: rows,
            total,
            hasMore,
            page: Number(payload?.page || payload?.data?.page || params.page || 1),
            limit: Number(payload?.limit || payload?.data?.limit || params.limit || rows.length),
            source:
              rows.length === 0
                ? (hasStatusFilter ? "devices-v2-status" : "devices-fast")
                : (payload?.source || payload?.data?.source || (hasStatusFilter ? "devices-v2-status" : "devices-fast")),
            counts: payload?.counts || payload?.data?.counts || null,
          },
        };
      },
      providesTags: ["Device", "Devices"],
    }),

    getAdminDevicesV2Page: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const params = {
          page: arg?.page ?? 1,
          limit: arg?.limit ?? 100,
          ...arg,
        };
        const result = await fetchWithBQ({ url: "/admin/devices/v2", params });
        if (result.error) {
          const fallback = await fetchWithBQ({
            url: "/devices",
            params: {
              page: params.page,
              limit: params.limit,
              includeRelations: "true",
              ...Object.fromEntries(
                Object.entries(params).filter(([key]) => !["page", "limit"].includes(key))
              ),
            },
          });
          if (fallback.error) return { error: result.error };
          const rows = Array.isArray(fallback.data?.data) ? fallback.data.data : Array.isArray(fallback.data) ? fallback.data : [];
          return {
            data: {
              data: rows,
              total: Number(fallback.data?.total || rows.length || 0),
              page: Number(fallback.data?.page || params.page || 1),
              limit: Number(fallback.data?.limit || params.limit || rows.length || 0),
              hasMore: Boolean(fallback.data?.hasMore),
              source: "v1-fallback",
            },
          };
        }

        const rows = adaptV2DeviceRows(result.data?.data);
        return {
          data: {
            data: rows,
            total: Number(result.data?.total || rows.length || 0),
            page: Number(result.data?.page || params.page || 1),
            limit: Number(result.data?.limit || params.limit || rows.length || 0),
            hasMore: Boolean(result.data?.hasMore),
            source: "v2",
          },
        };
      },
      providesTags: ["Device", "Devices"],
    }),

    getDeviceListPageV2: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const params = {
          page: arg?.page ?? 1,
          limit: arg?.limit ?? 100,
          ...arg,
        };
        delete params.scopeUserId;
        const normalizedStatus = normalizeFleetStatusKey(params.status);
        const wantsStatusFilter = Boolean(normalizedStatus);

        const primary = await fetchWithBQ({
          url: "/devices/v2/page",
          params: {
            ...params,
            ...(wantsStatusFilter ? { status: normalizedStatus } : {}),
          },
        });

        if (!primary.error) {
          return {
            data: {
              data: Array.isArray(primary.data?.data) ? primary.data.data : [],
              total: Number(primary.data?.total || 0),
              page: Number(primary.data?.page || params.page || 1),
              limit: Number(primary.data?.limit || params.limit || 0),
              totalPages: Number(primary.data?.totalPages || 0),
              counts: primary.data?.counts || null,
              source: primary.data?.source || "v2-page",
            },
          };
        }

        const compactEndpoint = normalizedStatus === "expired" ? "/devices/expired" : "/devices/compact";

        const compact = await fetchWithBQ({
          url: compactEndpoint,
          params: {
            ...params,
            ...(wantsStatusFilter && normalizedStatus !== "expired" ? { status: normalizedStatus } : {}),
          },
        });

        if (!compact.error) {
          const rows = Array.isArray(compact.data?.data)
            ? compact.data.data
            : Array.isArray(compact.data)
              ? compact.data
              : [];
          const total = Number(compact.data?.total || rows.length || 0);
          return {
            data: {
              data: rows,
              total,
              page: Number(compact.data?.page || params.page || 1),
              limit: Number(compact.data?.limit || params.limit || rows.length || 0),
              totalPages: Number(
                compact.data?.totalPages ||
                  Math.ceil(total / Math.max(1, Number(params.limit || 1)))
              ),
              counts: compact.data?.counts || null,
              filterMode: wantsStatusFilter ? "server" : "none",
              source: normalizedStatus === "expired" ? "expired-endpoint" : "compact",
            },
          };
        }

        const fetchLegacyV1 = async () =>
          fetchWithBQ({
            url: "/devices",
            params: {
              page: params.page,
              limit: params.limit,
              includeRelations: "true",
              ...Object.fromEntries(
                Object.entries(params).filter(([key]) => !["page", "limit"].includes(key))
              ),
            },
          });

        if (wantsStatusFilter) {
          const legacyV1Status = await fetchLegacyV1();
          if (!legacyV1Status.error) {
            const fallbackRows = Array.isArray(legacyV1Status.data?.data)
              ? legacyV1Status.data.data
              : Array.isArray(legacyV1Status.data)
                ? legacyV1Status.data
                : [];

            return {
              data: {
                data: fallbackRows,
                total: Number(legacyV1Status.data?.total || fallbackRows.length || 0),
                page: Number(legacyV1Status.data?.page || params.page || 1),
                limit: Number(legacyV1Status.data?.limit || params.limit || fallbackRows.length || 0),
                totalPages: Number(
                  legacyV1Status.data?.totalPages ||
                    Math.ceil(
                      Number(legacyV1Status.data?.total || fallbackRows.length || 0) /
                        Math.max(1, Number(params.limit || 1))
                    )
                ),
                counts: legacyV1Status.data?.counts || null,
                source: "devices-v1-status-fallback",
              },
            };
          }
        }

        const legacyV2 = await fetchWithBQ({
          url: "/admin/devices/v2",
          params,
        });

        if (!legacyV2.error) {
          const rows = adaptV2DeviceRows(legacyV2.data?.data);
          return {
            data: {
              data: rows,
              total: Number(legacyV2.data?.total || rows.length || 0),
              page: Number(legacyV2.data?.page || params.page || 1),
              limit: Number(legacyV2.data?.limit || params.limit || rows.length || 0),
              totalPages: Number(
                legacyV2.data?.totalPages ||
                  Math.ceil(Number(legacyV2.data?.total || rows.length || 0) / Math.max(1, Number(params.limit || 1)))
              ),
              counts: legacyV2.data?.counts || null,
              source: "admin-v2-fallback",
            },
          };
        }

        const legacyV1 = await fetchLegacyV1();

        if (legacyV1.error) {
          return { error: primary.error };
        }

        const fallbackRows = Array.isArray(legacyV1.data?.data)
          ? legacyV1.data.data
          : Array.isArray(legacyV1.data)
            ? legacyV1.data
            : [];

        return {
          data: {
            data: fallbackRows,
            total: Number(legacyV1.data?.total || fallbackRows.length || 0),
            page: Number(legacyV1.data?.page || params.page || 1),
            limit: Number(legacyV1.data?.limit || params.limit || fallbackRows.length || 0),
            totalPages: Number(
              legacyV1.data?.totalPages ||
                Math.ceil(Number(legacyV1.data?.total || fallbackRows.length || 0) / Math.max(1, Number(params.limit || 1)))
            ),
            counts: legacyV1.data?.counts || null,
            source: "devices-v1-fallback",
          },
        };
      },
      providesTags: ["Device", "Devices"],
    }),

    getDeviceRenewalsPageV2: builder.query({
      query: (params = {}) => ({
        url: "/devices/v2/renewals",
        params,
      }),
      transformResponse: (response) => ({
        data: Array.isArray(response?.data) ? response.data : [],
        total: Number(response?.total || 0),
        page: Number(response?.page || 1),
        limit: Number(response?.limit || 0),
        totalPages: Number(response?.totalPages || 0),
        counts: response?.counts || null,
      }),
      providesTags: ["Device", "Devices"],
    }),

    getDeviceExpiringPageV2: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const params = {
          page: arg?.page ?? 1,
          limit: arg?.limit ?? 100,
          ...arg,
        };

        const primary = await fetchWithBQ({
          url: "/devices/v2/expiring",
          params,
        });

        if (!primary.error) {
          return {
            data: {
              data: Array.isArray(primary.data?.data) ? primary.data.data : [],
              total: Number(primary.data?.total || 0),
              page: Number(primary.data?.page || params.page || 1),
              limit: Number(primary.data?.limit || 0),
              totalPages: Number(primary.data?.totalPages || 0),
              counts: primary.data?.counts || null,
              source: "v2-expiring",
            },
          };
        }

        const legacyAll = await fetchWithBQ({
          url: "/devices",
          params: {
            all: "true",
            includeRelations: "true",
            includeDeleted: "false",
          },
        });

        if (legacyAll.error) {
          return { error: primary.error };
        }

        const legacyRows = Array.isArray(legacyAll.data?.data)
          ? legacyAll.data.data
          : Array.isArray(legacyAll.data)
            ? legacyAll.data
            : [];

        const now = new Date();
        const from = params.from ? new Date(params.from) : null;
        const to = params.to ? new Date(params.to) : null;
        const category = String(params.category || params.days || "expired").trim().toLowerCase();

        const withExpiry = legacyRows.map((device) => {
          const expiryDate = device?.deviceLicense?.expiryDate ? new Date(device.deviceLicense.expiryDate) : null;
          const diffDays = expiryDate && !Number.isNaN(expiryDate.getTime())
            ? Math.ceil((new Date(expiryDate.setHours(23, 59, 59, 999)).getTime() - now.getTime()) / 86400000)
            : null;
          return { ...device, expiryDiffDays: diffDays };
        });

        const filtered = withExpiry.filter((device) => {
          const expiryDate = device?.deviceLicense?.expiryDate ? new Date(device.deviceLicense.expiryDate) : null;
          if (!expiryDate || Number.isNaN(expiryDate.getTime())) return false;
          if (from && expiryDate < from) return false;
          if (to && expiryDate > to) return false;

          if (category === "expired") {
            return device.expiryDiffDays != null && device.expiryDiffDays < 0;
          }

          const days = Number(category);
          return (
            device.expiryDiffDays != null &&
            device.expiryDiffDays >= 0 &&
            device.expiryDiffDays <= days
          );
        });

        filtered.sort((a, b) => {
          const aTime = a?.deviceLicense?.expiryDate ? new Date(a.deviceLicense.expiryDate).getTime() : 0;
          const bTime = b?.deviceLicense?.expiryDate ? new Date(b.deviceLicense.expiryDate).getTime() : 0;
          return aTime - bTime;
        });

        const limitNum = Number(params.limit || 0);
        const wantsAll = String(params.all || "").toLowerCase() === "true" || limitNum === 0;
        const total = filtered.length;
        const paged = wantsAll
          ? filtered
          : filtered.slice((params.page - 1) * limitNum, (params.page - 1) * limitNum + limitNum);

        return {
          data: {
            data: paged,
            total,
            page: Number(params.page || 1),
            limit: wantsAll ? total : limitNum,
            totalPages: wantsAll ? 1 : Math.max(1, Math.ceil(total / Math.max(1, limitNum))),
            counts: null,
            source: "devices-v1-expiring-fallback",
          },
        };
      },
      providesTags: ["Device", "Devices"],
    }),

    // GET /devices - All devices (paginated loop to avoid timeouts on large fleets)
    getAllDevices: builder.query({
      queryFn: buildPagedDeviceAccumulatorQuery({
        pageLimit: 200,
        defaultLite: false,
        defaultIncludeRelations: true,
        allowPartialOnLaterError: false,
        persistKeyPrefix: "devices-full",
      }),
      providesTags: ["Device", "Devices"],
    }),

    // GET /devices - Shared lite query for selectors, pickers, and assignment UIs.
    // Keeps full-query behavior untouched for complex screens that still need it.
    getAllLiteDevices: builder.query({
      queryFn: buildPagedDeviceAccumulatorQuery({
        pageLimit: 500,
        defaultLite: true,
        allowPartialOnLaterError: true,
        persistKeyPrefix: "devices-lite-v3",
      }),
      keepUnusedDataFor: 300,
      providesTags: ["Device", "Devices"],
    }),

    // GET /devices - Single lite page fetch for lightweight pickers with server-side search.
    getLiteDevicesPage: builder.query({
      async queryFn(arg = {}, _api, _extra, fetchWithBQ) {
        const params = {
          page: arg?.page ?? 1,
          limit: arg?.limit ?? 50,
          lite: "true",
          ...arg,
        };
        const response = await fetchWithBQ({ url: "/devices", params });
        if (response.error) {
          return { error: response.error };
        }

        const payload = response.data;
        const rows = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.data?.data)
            ? payload.data.data
            : Array.isArray(payload)
              ? payload
              : [];

        return {
          data: {
            data: rows,
            total: Number(payload?.total || payload?.data?.total || rows.length || 0),
            page: Number(payload?.page || payload?.data?.page || params.page || 1),
            limit: Number(payload?.limit || payload?.data?.limit || params.limit || rows.length || 0),
            hasMore: Boolean(payload?.hasMore ?? payload?.data?.hasMore),
            source: payload?.source || payload?.data?.source || "devices-lite-page",
          },
        };
      },
      providesTags: ["Device", "Devices"],
    }),

    // ✅ GET /users/my-devices - Current user's devices
    getMyDevices: builder.query({
      query: (params) => ({
        url: '/users/my-devices',
        params,
      }),
      providesTags: ['Devices'],
      transformResponse: (response) => {
        // Return { data: [...] } format
        return {
          data: response?.data || response?.devices || response || []
        };
      }
    }),

    // GET /devices/:id
    getDeviceById: builder.query({
      query: (id) => ({ url: `/devices/${id}` }),
      providesTags: (result, error, id) => [{ type: 'Device', id }],
    }),

    getInstallationCertificate: builder.query({
      query: (deviceId) => ({ url: `/devices/${deviceId}/installation-certificate` }),
      providesTags: (result, error, deviceId) => [{ type: "Device", id: deviceId }],
    }),

    // POST /devices
    addDevice: builder.mutation({
      query: (deviceData) => ({
        url: '/devices',
        method: 'POST',
        body: deviceData,
      }),
      invalidatesTags: ['AdminData', 'Device', 'Devices'], 
    }),

    // PUT /devices/:deviceId
    editDevice: builder.mutation({
      query: ({ deviceId, ...patch }) => ({
        url: `/devices/${deviceId}`,
        method: 'PUT',
        body: patch,
      }),
      async onQueryStarted(_arg, { getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          const authState = getState?.()?.auth || {};
          clearPersistedDeviceCachesByPrefixes([
            buildAuthScopedDeviceCachePrefix("devices-lite-v3", authState),
            buildAuthScopedDeviceCachePrefix("devices-full", authState),
          ]);
        } catch (_) {
          // Let the mutation error flow handle UI feedback.
        }
      },
      invalidatesTags: (result, error, { deviceId }) => [
        { type: 'AdminData' }, 
        { type: 'Device', id: deviceId },
        { type: 'Devices' }
      ], 
    }),

    createInstallationCertificate: builder.mutation({
      query: ({ deviceId, formData }) => ({
        url: `/devices/${deviceId}/installation-certificate`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: (result, error, { deviceId }) => [
        { type: "Device", id: deviceId },
        { type: "Devices" },
      ],
    }),

    // DELETE /devices/:deviceId
    deleteDevice: builder.mutation({
      query: (deviceId) => ({
        url: `/devices/${deviceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminData', 'Device', 'Devices'],
    }),

    bulkDeleteDevices: builder.mutation({
      query: (deviceIds) => ({
        url: '/devices/bulk-delete',
        method: 'DELETE',
        body: { deviceIds },
      }),
      invalidatesTags: ['AdminData', 'Device', 'Devices'],
    }),

    restoreDevice: builder.mutation({
      query: (deviceId) => ({
        url: `/devices/${deviceId}/restore`,
        method: 'PUT',
      }),
      invalidatesTags: ['AdminData', 'Device', 'Devices'],
    }),

    permanentlyDeleteDevice: builder.mutation({
      query: (deviceId) => ({
        url: `/devices/${deviceId}/permanent`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdminData', 'Device', 'Devices'],
    }),

    // GET /devices/assets/validate-imei/:imei
    validateIMEI: builder.query({
      query: (imei) => ({ url: `/devices/assets/validate-imei/${imei}` }),
    }),

    // POST /:id/command
    sendCommand: builder.mutation({
      query: ({ deviceId, command }) => ({
        url: `/devices/${deviceId}/command`,
        method: "POST",
        body: { command },
      }),
    }),

    // GET /devices/events
    getAllEvents: builder.query({
      query: () => ({ url: "/devices/events" }),
    }),

    getSharedGeofences: builder.query({
      query: (params = {}) => ({
        url: "/devices/geofences",
        params,
      }),
      transformResponse: (response) => response?.data || [],
      providesTags: (result) =>
        result
          ? [
              { type: "Geofence", id: "LIST" },
              ...result.map((item) => ({ type: "Geofence", id: item._id || item.id })),
            ]
          : [{ type: "Geofence", id: "LIST" }],
    }),

    createSharedGeofence: builder.mutation({
      query: (payload) => ({
        url: "/devices/geofences",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Geofence", id: "LIST" }, "Devices", "Device"],
    }),

    updateSharedGeofence: builder.mutation({
      query: ({ geofenceId, ...payload }) => ({
        url: `/devices/geofences/${geofenceId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (result, error, { geofenceId }) => [
        { type: "Geofence", id: "LIST" },
        { type: "Geofence", id: geofenceId },
        "Devices",
        "Device",
      ],
    }),

    deleteSharedGeofence: builder.mutation({
      query: (geofenceId) => ({
        url: `/devices/geofences/${geofenceId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Geofence", id: "LIST" }, "Devices", "Device"],
    }),

    assignSharedGeofenceDevice: builder.mutation({
      query: ({ geofenceId, deviceId }) => ({
        url: `/devices/geofences/${geofenceId}/assign-device`,
        method: "POST",
        body: { deviceId },
      }),
      invalidatesTags: (result, error, { geofenceId, deviceId }) => [
        { type: "Geofence", id: "LIST" },
        { type: "Geofence", id: geofenceId },
        { type: "Device", id: deviceId },
        "Devices",
      ],
    }),

    unassignSharedGeofenceDevice: builder.mutation({
      query: ({ geofenceId, deviceId }) => ({
        url: `/devices/geofences/${geofenceId}/assign-device/${deviceId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { geofenceId, deviceId }) => [
        { type: "Geofence", id: "LIST" },
        { type: "Geofence", id: geofenceId },
        { type: "Device", id: deviceId },
        "Devices",
      ],
    }),

    getLandmarks: builder.query({
      query: (params = {}) => ({
        url: "/poi",
        params: {
          kind: "landmark",
          ...params,
        },
      }),
      transformResponse: (response) => response?.data || [],
      providesTags: (result) =>
        result
          ? [
              { type: "Landmark", id: "LIST" },
              ...result.map((item) => ({ type: "Landmark", id: item._id || item.id })),
            ]
          : [{ type: "Landmark", id: "LIST" }],
    }),

    createLandmark: builder.mutation({
      query: (payload) => ({
        url: "/poi",
        method: "POST",
        body: {
          kind: "landmark",
          ...payload,
        },
      }),
      invalidatesTags: [{ type: "Landmark", id: "LIST" }],
    }),

    updateLandmark: builder.mutation({
      query: ({ landmarkId, ...payload }) => ({
        url: `/poi/${landmarkId}`,
        method: "PUT",
        body: {
          kind: "landmark",
          ...payload,
        },
      }),
      invalidatesTags: (result, error, { landmarkId }) => [
        { type: "Landmark", id: "LIST" },
        { type: "Landmark", id: landmarkId },
      ],
    }),

    deleteLandmark: builder.mutation({
      query: (landmarkId) => ({
        url: `/poi/${landmarkId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Landmark", id: "LIST" }],
    }),

    assignLandmarkDevice: builder.mutation({
      query: ({ landmarkId, deviceId, entryAlert = false, exitAlert = false }) => ({
        url: `/poi/${landmarkId}/assign-device`,
        method: "POST",
        body: {
          deviceId,
          entryAlert,
          exitAlert,
        },
      }),
      invalidatesTags: (result, error, { landmarkId, deviceId }) => [
        { type: "Landmark", id: "LIST" },
        { type: "Landmark", id: landmarkId },
        { type: "Device", id: deviceId },
        "Devices",
      ],
    }),

    unassignLandmarkDevice: builder.mutation({
      query: ({ landmarkId, deviceId }) => ({
        url: `/poi/${landmarkId}/assign-device/${deviceId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { landmarkId, deviceId }) => [
        { type: "Landmark", id: "LIST" },
        { type: "Landmark", id: landmarkId },
        { type: "Device", id: deviceId },
        "Devices",
      ],
    }),
    
    // ═══════════════════════════════════════════════════════════════════
    // ASSET ENDPOINTS
    // ═══════════════════════════════════════════════════════════════════

    // GET /devices/assets
    getAllAssets: builder.query({
      query: () => ({ url: "/devices/assets" }),
      providesTags: ["Asset"],
    }),

    // POST /devices/assets
    addAsset: builder.mutation({
      query: (assetData) => ({
        url: '/devices/assets',
        method: 'POST',
        body: assetData,
      }),
      invalidatesTags: ['Asset'],
    }),
    
    // PUT /devices/assets/:assetId
    editAsset: builder.mutation({
      query: ({ assetId, ...patch }) => ({
        url: `/devices/assets/${assetId}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: ['Asset'], 
    }),
    
    // PUT /devices/assets/:assetId - Status update
    updateAssetStatus: builder.mutation({
      query: ({ assetId, status }) => ({
        url: `/devices/assets/${assetId}`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: ['Asset'],
    }),
    
    // DELETE /devices/assets/:assetId
    deleteAsset: builder.mutation({
      query: (assetId) => ({
        url: `/devices/assets/${assetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Asset'],
    }),

    // DELETE /devices/assets/:assetId (Permanent)
    permanentDeleteAsset: builder.mutation({
      query: (assetId) => ({
        url: `/devices/assets/${assetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Asset'],
    }),
    getDeviceAddress: builder.query({
      query: (arg) => {
        const payload =
          arg && typeof arg === "object"
            ? arg
            : { deviceId: arg };
        const { deviceId, lat, lng } = payload || {};

        return {
          url: `/devices/${deviceId}/address`,
          params: {
            ...(Number.isFinite(Number(lat)) ? { lat: Number(lat) } : {}),
            ...(Number.isFinite(Number(lng)) ? { lng: Number(lng) } : {}),
          },
        };
      },
      // Don't cache - always fetch fresh
      keepUnusedDataFor: 0,
    }),

    // PUT /devices/:deviceId/advanced-settings
    updateDeviceAdvancedSettings: builder.mutation({
      query: ({ deviceId, ...patch }) => ({
        url: `/devices/${deviceId}/advanced-settings`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: (result, error, { deviceId }) => [
        { type: "AdminData" },
        { type: "Device", id: deviceId },
        { type: "Devices" },
      ],
    }),
    
    // POST /devices/assets/bulk-upload
    bulkUploadAssets: builder.mutation({
      query: (formData) => ({
        url: '/devices/assets/bulk-upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Asset'],
    }),

  }),
});

// Export hooks
export const {
  // Admin Data
  useGetAdminDataQuery,
  
  // User Management
  useCreateUserMutation,
  useGetAllUsersQuery,
  useGetAssignableUsersQuery,
  useEditUserMutation,
  useDeleteUserMutation,
  useRestoreUserMutation,
  useResetPasswordMutation,
  useGetDeviceNotificationTemplateQuery,
  useGetManagedUserDeviceAlertPreferencesQuery,
  useGetManagedUserAlertSummaryQuery,
  useUpdateManagedUserDeviceAlertPreferencesMutation,
  useUpdateManagedUserAlertSummaryMutation,
  useRevealUserPasswordMutation,
  useAssignUserToGroupAdminMutation,
  useRemoveManagedUserMutation,
  useImpersonateUserMutation,
  
  // Device Management
  useGetAllDevicesQuery,
  useLazyGetAllDevicesQuery,
  useGetAllLiteDevicesQuery,
  useLazyGetAllLiteDevicesQuery,
  useGetLiteDevicesPageQuery,
  useGetDevicesPageQuery,
  useLazyGetDevicesPageQuery,
  useGetAdminDevicesV2PageQuery,
  useLazyGetAdminDevicesV2PageQuery,
  useGetDeviceListPageV2Query,
  useLazyGetDeviceListPageV2Query,
  useGetDeviceRenewalsPageV2Query,
  useLazyGetDeviceRenewalsPageV2Query,
  useGetDeviceExpiringPageV2Query,
  useLazyGetDeviceExpiringPageV2Query,
  useGetMyDevicesQuery,
  useLazyGetMyDevicesQuery,
  useGetDeviceByIdQuery,
  useLazyGetDeviceByIdQuery,
  useGetInstallationCertificateQuery,
  useAddDeviceMutation,
  useEditDeviceMutation,
  useCreateInstallationCertificateMutation,
  useDeleteDeviceMutation,
  useBulkDeleteDevicesMutation,
  useRestoreDeviceMutation,
  usePermanentlyDeleteDeviceMutation,
  useValidateIMEIQuery,
  useLazyValidateIMEIQuery,
  useLazyGetDeviceAddressQuery,
  useSendCommandMutation,
  useGetAllEventsQuery,
  useGetSharedGeofencesQuery,
  useCreateSharedGeofenceMutation,
  useUpdateSharedGeofenceMutation,
  useDeleteSharedGeofenceMutation,
  useAssignSharedGeofenceDeviceMutation,
  useUnassignSharedGeofenceDeviceMutation,
  useGetLandmarksQuery,
  useCreateLandmarkMutation,
  useUpdateLandmarkMutation,
  useDeleteLandmarkMutation,
  useAssignLandmarkDeviceMutation,
  useUnassignLandmarkDeviceMutation,
  
  // Asset Management
  useGetAllAssetsQuery,
  useAddAssetMutation,
  useEditAssetMutation,
  useUpdateAssetStatusMutation,
  useDeleteAssetMutation,
  usePermanentDeleteAssetMutation,
  useBulkUploadAssetsMutation,

  useGetDeviceAddressQuery,
  useUpdateDeviceAdvancedSettingsMutation,
  useLazyGetViewportVehiclesQuery,
} = adminApi;
